import React, { useState } from 'react';
import { toLocalDateString, parseLocalDate, getDayName, getTodayLocal } from '../utils/dateUtils';

interface VisitScheduleEntry {
  date: string;
  isVisitDay: boolean;
  notes?: string;
}

interface SingleJobCalendarProps {
  jobTitle: string;
  customerName: string;
  startDate: string;
  endDate: string;
  visitSchedule?: VisitScheduleEntry[];
  contractRate: number;
  contractType: string;
}

const SingleJobCalendar: React.FC<SingleJobCalendarProps> = ({
  jobTitle,
  customerName,
  startDate,
  endDate,
  visitSchedule,
  contractRate,
  contractType
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    const start = parseLocalDate(startDate);
    return now >= start ? now : start;
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);

  // Get days in a month as calendar grid
  const getCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty slots for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  // Check if a date is a work day
  const getDayInfo = (date: Date): { isWorkDay: boolean; notes?: string; isPast: boolean } => {
    const checkDate = parseLocalDate(date);
    const dateStr = toLocalDateString(checkDate);
    const jobStart = parseLocalDate(startDate);
    const jobEnd = parseLocalDate(endDate);
    const today = getTodayLocal();

    // Check if date is within job period
    if (checkDate < jobStart || checkDate > jobEnd) {
      return { isWorkDay: false, isPast: checkDate < today };
    }

    // Check if this specific day is cancelled or has notes
    const visitScheduleEntry = visitSchedule?.find(vs => {
      const vsDateStr = toLocalDateString(vs.date);
      return vsDateStr === dateStr;
    });

    // If this day is explicitly cancelled, return false
    if (visitScheduleEntry && visitScheduleEntry.isVisitDay === false) {
      return { isWorkDay: false, notes: visitScheduleEntry.notes, isPast: checkDate < today };
    }

    // Default: all days within period are work days
    return { isWorkDay: true, notes: visitScheduleEntry?.notes, isPast: checkDate < today };
  };

  // Navigate months
  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + 1);
      return next;
    });
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(prev => {
      const previous = new Date(prev);
      previous.setMonth(previous.getMonth() - 1);
      return previous;
    });
  };

  // Check if date is today
  const isToday = (date: Date): boolean => {
    const today = getTodayLocal();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="mb-4 pb-4 border-b border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{jobTitle}</h3>
        <p className="text-sm text-gray-600">Customer: {customerName}</p>
        <p className="text-sm text-primary-600 font-medium">
          ৳{contractRate.toLocaleString()}
        </p>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPreviousMonth}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h4 className="text-lg font-semibold text-gray-900">
          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h4>
        <button
          onClick={goToNextMonth}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
          <div key={i} className="text-xs font-semibold text-gray-500 text-center py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {getCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth()).map((date, i) => {
          if (!date) return <div key={i} className="aspect-square"></div>;
          
          const { isWorkDay, notes, isPast } = getDayInfo(date);
          const isTodayDate = isToday(date);
          
          return (
            <div
              key={i}
              onClick={() => {
                if (isWorkDay) {
                  setSelectedDate(date);
                  setShowNotesModal(true);
                }
              }}
              className={`aspect-square relative rounded-lg transition-all border-2 ${
                isTodayDate && isWorkDay
                  ? 'border-primary-500 bg-primary-100 ring-2 ring-primary-300'
                  : isTodayDate && !isWorkDay
                  ? 'border-gray-400 bg-gray-100'
                  : isWorkDay
                  ? isPast
                    ? 'border-green-200 bg-green-50 opacity-60'
                    : 'border-green-300 bg-green-50 hover:bg-green-100 cursor-pointer'
                  : isPast
                  ? 'border-gray-200 bg-gray-50 opacity-50'
                  : 'border-red-200 bg-red-50'
              }`}
              title={
                isWorkDay 
                  ? notes 
                    ? `Work day - Has notes` 
                    : 'Work day' 
                  : 'Day off / Cancelled'
              }
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center p-1">
                <span className={`text-sm font-semibold ${
                  isTodayDate
                    ? 'text-primary-700'
                    : isWorkDay
                    ? 'text-green-800'
                    : 'text-gray-400'
                }`}>
                  {date.getDate()}
                </span>
                {notes && (
                  <div className="mt-1">
                    <svg className="w-3 h-3 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                      <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-6 text-xs flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-primary-100 border-2 border-primary-500 rounded"></div>
          <span className="text-gray-600">Today</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-50 border-2 border-green-300 rounded"></div>
          <span className="text-gray-600">Work Day</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-50 border-2 border-red-200 rounded"></div>
          <span className="text-gray-600">Day Off</span>
        </div>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
          </svg>
          <span className="text-gray-600">Has Notes</span>
        </div>
      </div>

      {/* Contract Period Info */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-4">
          <h5 className="text-sm font-semibold text-primary-900 mb-2">Contract Period</h5>
          <p className="text-sm text-primary-800">
            {parseLocalDate(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {' '}-{' '}
            {parseLocalDate(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Notes Modal */}
      {showNotesModal && selectedDate && (() => {
        const { notes } = getDayInfo(selectedDate);
        return notes ? (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 rounded-t-2xl flex justify-between items-center">
                <h3 className="text-lg font-bold text-white">
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h3>
                <button
                  onClick={() => setShowNotesModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6">
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-yellow-900 mb-2">Special Notes:</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{notes}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200">
                <button
                  onClick={() => setShowNotesModal(false)}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : null;
      })()}
    </div>
  );
};

export default SingleJobCalendar;
